import React, { FC, useCallback } from "react";
import { 
  Box, 
  Button, 
  CircularProgress, 
  FormControl, 
  FormHelperText, 
  Grid, 
  InputLabel, 
  MenuItem, 
  Select, 
  SelectChangeEvent, 
  Stack, 
  TextField 
} from "@mui/material";
import { v4 as uuidv4 } from 'uuid'; 

import CustomModal from "../../../components/custom-modal/CustomModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { setOpenSpecialAddModal } from "../slices/specialAddModalSlice";
import i18n from "i18next";
import SpecialAddScenarios from "../../../constants/specialAddScenarios";
import { useForm, FormProvider, Controller, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchQuestionsByScenario, Question, Variable, resetError, resetState } from "../slices/specialArticleSlice";
import { SaveSpecialArticleInput, SpecialArticle, saveSpecialArticle } from "../slices/saveSpecialArticleSlice";
import { addSelectedArticle, ArticleWithQuantity } from "../slices/articleStandardSlice";
import { openSnackbar  } from "../../../redux/slices/snackbarSlice";
import { useTranslation } from "react-i18next";
import { number, object, optional, string, z } from "zod";
import BuildSpecialForm from "./BuildSpecialForm";
type ErrorPayload = {
  message: string;
};
export interface AddSpecialArticleInput {
  scenario: (typeof SpecialAddScenarios)[number]["value"];
  quantity: number;
  [key: string]: any;
}

const useValidationSchema = (questions: Question[], variables: Variable[]) => {
  const schema: any = {
    scenario: string().min(1, i18n.t("newPreOrder.validation.requiredField")),
    quantity: number().min(1, `${i18n.t("newPreOrder.validation.minValue")} 1`),
  };

  questions.forEach((item) => {
    switch (item.TypeReponse) {
      case "DROPDOWN":
        schema[item.QuestionCode] = optional(string());
        break;
      case "MINMAX":
        schema[item.QuestionCode] = z
          .union([
            z
              .number()
              .min(item.MINVALUE ?? 0, {
                message: `${i18n.t("newPreOrder.validation.minValue")}${item.MINVALUE ?? 0}`,
              })
              .max(item.MAXVALUE ?? 1000, {
                message: `${i18n.t("newPreOrder.validation.maxValue")}${item.MAXVALUE ?? 1000}`,
              }),
            z.string(),
          
          ])
          .optional();
        break;
      case "CHECKBOX":
        schema[item.QuestionCode] = z.union([z.boolean(), z.string()]).optional();
        break;
      default:
        schema[item.QuestionCode] = optional(string());
    }
  });

  variables.forEach((variable) => {
    schema[variable.LineNumber] = optional(string());
  });

  return object(schema);
};

const prepareDefaultValues = (questions: Question[], variables: Variable[]) => {
  const defaultValues: { [key: string]: any } = {
    scenario: "",
    quantity: 1,  
  };

  questions.forEach((item) => {
    switch (item.TypeReponse) {
      case "DROPDOWN":
        defaultValues[item.QuestionCode] = "";
        break;
      case "MINMAX":
        defaultValues[item.QuestionCode] = 0;
        break;
      case "CHECKBOX":
        defaultValues[item.QuestionCode] = false;
        break;
      default:
        defaultValues[item.QuestionCode] = "";
    }
  });

  variables.forEach((variable) => {
    defaultValues[variable.LineNumber] = "";
  });

  return defaultValues;
};

const extractAttributes = (input: AddSpecialArticleInput): { questionName: string; questionValue: string }[] => {
  return Object.keys(input)
    .filter((key) => key !== "scenario" && key !== "quantity")
    .map((key) => ({
      questionName: key,
      questionValue: input[key]?.toString() ?? "",
    }));
};

const areAllActiveQuestionsFilled = (questions: Question[]): boolean => {
  return questions.every(
    (question) => !question.isActif || (question.isActif && question.val && question.val.trim() !== "")
  );
};

const SpecialAddModal: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isOpen } = useAppSelector((state) => state.specialAddModalSlice);
  const { data, status: fetchStatus } = useAppSelector((state) => state.specialArticleSlice);
  const { status: saveStatus } = useAppSelector((state) => state.saveSpecialArticleSlice);
  const { client, siteExpedition, siteVente } = useAppSelector((state) => state.preOrderFilterSlice);

  const validationSchema = useValidationSchema(data?.Questions || [], data?.Variables || []);
  const defaultValues = prepareDefaultValues(data?.Questions || [], data?.Variables || []);

  const methods = useForm<AddSpecialArticleInput>({
    resolver: zodResolver(validationSchema),
    defaultValues,
    mode: "all",
  });

  const { control, handleSubmit, reset, watch, formState: { errors } } = methods;

  const scenario = watch("scenario");

  const onSubmit: SubmitHandler<AddSpecialArticleInput> = async (data) => {
    if (siteVente?.Code && siteExpedition?.Code && client?.NumeroClient) {
      const extractedAttributes = extractAttributes(data);

      const body: SaveSpecialArticleInput = {
        scenario: data.scenario,
        qty: data.quantity,
        siteVente: siteVente.Code,
        siteExpedition: siteExpedition.Code,
        numeroClient: client.NumeroClient,
        listQR: extractedAttributes,
      };

      dispatch(saveSpecialArticle(body)).then((result) => {
        if (saveSpecialArticle.fulfilled.match(result)) {

          const rawArticleString = result.payload.data;

         
          
          // Si c’est un string JSON, on doit parser :
          const rawArticle = typeof rawArticleString === "string"
            ? JSON.parse(rawArticleString)
            : rawArticleString;
          const savedArticle: SpecialArticle = {
            numeroArticle: rawArticle.NumeroArticle,
            libelleArticle: rawArticle.LibelleArticle,
            uniteArticle: rawArticle.UniteArticle,
            prixArticle: rawArticle.PrixArticle,
            qtyArticle: rawArticle.QtyArticle,
          };
          
       
          
          
          const quantity = parseInt(savedArticle.qtyArticle, 10);
          
          const newArticle: ArticleWithQuantity = {
            id: uuidv4(),
            Reference: savedArticle.numeroArticle,
            Description: savedArticle.libelleArticle,
            Unite: savedArticle.uniteArticle,
            price: parseFloat(savedArticle.prixArticle.replace(",", ".")),
            quantity: quantity,
            typeArticle: "special",
          };
          dispatch(addSelectedArticle(newArticle));
          handleCloseModal();
          dispatch(
            openSnackbar({
              message: result.payload.message || "Article ajouté avec succès",
              type: "success",
            })
          );
        } else if (saveSpecialArticle.rejected.match(result)) {
          const errorPayload = result.payload as ErrorPayload; // Cast explicite ici        
          dispatch(
            openSnackbar({
              message: errorPayload.message || "Erreur lors de l'ajout de l'article",
              type: "error",
            })
          );
        }
      });
    }
  };

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleCloseModal = () => {
    dispatch(setOpenSpecialAddModal(false));
    dispatch(resetState());
    handleReset();
  };

  const handleChangeScenario = (event: SelectChangeEvent<string>, onChange: (value: string) => void) => {
    onChange(event.target.value);
    if (event.target.value && event.target.value !== "") {
      dispatch(fetchQuestionsByScenario(event.target.value)).then((result) => {
        if (fetchQuestionsByScenario.rejected.match(result)) {
          dispatch(
            openSnackbar({
              message: "Erreur lors de la récupération des données!",
              type: "error",
            })
          );
          dispatch(resetError());
        }
      });
    }
  };

  const allQuestionsFilled = areAllActiveQuestionsFilled(data?.Questions || []);

  return (
    <CustomModal
      sx={{
        minWidth: 500,
        maxWidth: 500,
        position: "absolute",
        top: "30%",
        left: "50%",
        transform: "translate(-50%, -30%)",
        height: "auto",
      }}
      isOpen={isOpen}
      handleClose={handleCloseModal}
      title={t("newPreOrder.buttons.specialAdd")}
    >
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {saveStatus === "loading" && (
            <Box sx={{ mt: 3, mb: 5 }} display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          )}
          {saveStatus !== "loading" && (
            <Grid
              container
              rowSpacing={2}
              sx={{
                maxHeight: "70vh",
                width: "100%",
                overflowY: "auto",
                padding: "2% 2%",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Grid item xs={12}>
                <InputLabel htmlFor="scenario">
                  {i18n.t("newPreOrder.chooseScenario")}
                </InputLabel>
                <Controller
                  name="scenario"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      fullWidth
                      variant="outlined"
                      error={!!errors.scenario}
                      disabled={fetchStatus === "loading"}
                    >
                      <Select
                        {...field}
                        labelId="selectScenario"
                        value={field.value}
                        onChange={(event) => handleChangeScenario(event, field.onChange)}
                        label={i18n.t("newPreOrder.chooseScenario")}
                      >
                        {SpecialAddScenarios.map((scenario) => (
                          <MenuItem key={scenario.value} value={scenario.value}>
                            {scenario.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.scenario && (
                        <FormHelperText color="red">
                          {errors.scenario.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor="quantity">
                  {i18n.t("newPreOrder.quantity")}
                </InputLabel>
                <Controller
                  name="quantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="quantity"
                      type="number"
                      variant="outlined"
                      fullWidth
                      required
                      inputProps={{ min: 1, step: 1 }}  
                      onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      error={!!errors?.quantity}
                      helperText={errors.quantity?.message}
                      disabled={fetchStatus === "loading"}
                    />
                  )}
                />
              </Grid>
              {fetchStatus === "loading" && (
                <Grid item xs={12}>
                  <Box sx={{ my: 3 }} display="flex" justifyContent="center" alignItems="center">
                    <CircularProgress />
                  </Box>
                </Grid>
              )}
              {fetchStatus === "succeeded" && scenario && data && (
                <BuildSpecialForm
                  data={data}
                  control={control}
                  errors={errors}
                />
              )}
            </Grid>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                sx={{
                  color: "#000000",
                  backgroundColor: "#DDDDDD",
                  ":hover": { backgroundColor: "#DDDDDD", opacity: 0.8 },
                }}
                onClick={handleCloseModal}
                disableElevation
              >
                {t("newPreOrder.buttons.return")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                disableElevation
                disabled={
                  fetchStatus !== "succeeded" ||
                  saveStatus === "loading" ||
                  !allQuestionsFilled
                }
              >
                {t("newPreOrder.buttons.add")}
              </Button>
            </Stack>
          </Box>
        </Box>
      </FormProvider>
    </CustomModal>
  );
};

export default SpecialAddModal;
